"use client";

import { useEffect, useState } from "react";
import { Feld, FeldHuelle, type FeldAttribute, type FeldEigenschaften } from "./feld";
import { baueAuswahlKonfiguration, type HsModus, type HsOption } from "./auswahl-konfiguration";
import { binde as bindeSchwebend } from "./schwebende-liste";

/**
 * Erweiterte Auswahl (M-002 F6): Preline `advanced-select` mit Suche im Dropdown, Zähler und Optionsvorlage.
 * Ab etwa 8 Optionen (`SUCHE_AB`) erscheint die Suche von selbst; kürzere Listen gehören in die native `Auswahl`.
 *
 * Verhalten: Das native `<select>` bleibt die Quelle des Wertes (Name, Formular-Absenden, `onChange` über das
 * Änderungs-Ereignis, das Preline auslöst). Das Plugin baut Auslöser und Liste daneben; initialisiert wird über
 * `HSSelect.autoInit()` (idempotent) beim Einhängen. `PrelineClient` baut die Instanzen bei Seitenwechsel ab und
 * neu auf, damit nichts doppelt entsteht. Ändert sich die Optionsliste, setzt `key` den Baustein neu auf.
 *
 * Pflicht: Der Baustein setzt bewusst kein natives `required` (ein verstecktes Pflichtfeld blockiert das Absenden
 * ohne sichtbare Meldung). Der Aufrufer prüft und zeigt den Feldfehler (`fehler`, F3).
 * Rechte: Die Optionen kommen vom Aufrufer, gefiltert mit derselben Prüffunktion wie die Action (r109, r110).
 */
export const SUCHE_AB = 8;

type HsRest = {
  name?: string;
  /** Vorgewählter Wert (einzeln: Text, mehrfach/tags: Liste). */
  vorgabe?: string | string[];
  deaktiviert?: boolean;
  /** Meldet die Wahl nach jeder Änderung (einzeln: Wert, mehrfach: Liste der Werte). */
  beiAenderung?: (wert: string | string[]) => void;
};

export interface HsAuswahlEigenschaften extends HsRest {
  attribute: FeldAttribute;
  modus: HsModus;
  optionen: HsOption[];
  platzhalter: string;
  mitSuche: boolean;
  suchePlatzhalter?: string;
  keineTreffer?: string;
  zaehlerText?: string;
  leerLabel?: string;
}

function optionsSchluessel(modus: HsModus, optionen: HsOption[], extra: string): string {
  return `${modus}|${extra}|${optionen.map((o) => `${o.wert}:${o.label}:${o.beschreibung ?? ""}:${o.gruppe ?? ""}`).join("|")}`;
}

/** Gemeinsamer Unterbau für `AuswahlErweitert`, `PersonenAuswahl` (P1, P3) und `TagAuswahl`. */
export function HsAuswahl({
  attribute: a,
  modus,
  optionen,
  platzhalter,
  mitSuche,
  suchePlatzhalter,
  keineTreffer,
  zaehlerText,
  leerLabel,
  name,
  vorgabe,
  deaktiviert,
  beiAenderung,
}: HsAuswahlEigenschaften) {
  const konfiguration = baueAuswahlKonfiguration({
    id: a.id,
    modus,
    optionen,
    platzhalter,
    mitSuche,
    suchePlatzhalter,
    keineTreffer,
    zaehlerText,
    groesse: a.groesse,
    fehlerhaft: a.fehlerhaft,
    beschreibung: a["aria-describedby"],
    leerLabel,
  });
  const schluessel = optionsSchluessel(modus, optionen, `${platzhalter}|${leerLabel ?? ""}|${a.fehlerhaft}|${a.groesse}`);

  // Server und erste Client-Ausgabe tragen nur den Platzhalter; das native Feld entsteht erst nach dem Einhängen.
  // Grund: `HSSelect.autoInit()` arbeitet auf dem ganzen Dokument und baut jedes native `data-hs-select`-Feld um,
  // auch eines, das React noch nicht hydriert hat (Server-HTML ≠ erste Client-Ausgabe → „Hydration failed“).
  const [eingehaengt, setzeEingehaengt] = useState(false);
  useEffect(() => setzeEingehaengt(true), []);

  useEffect(() => {
    if (!eingehaengt) return;
    let abgebrochen = false;
    let loesen: (() => void) | null = null;
    // autoInit ist idempotent (bereits erfasste Elemente bleiben); Fehler im Plugin dürfen das Formular nicht stören,
    // das native Feld bleibt als Rückfall im Formular (nur dann sichtbar, wenn `hidden` entfällt).
    void import("preline/non-auto")
      .then(({ HSSelect }) => {
        if (abgebrochen) return;
        HSSelect.autoInit();
        // Die offene Liste liegt fest am Fenster, damit sie im scrollenden Modal-Körper nicht abgeschnitten wird.
        const aufheben = bindeSchwebend(`${a.id}-nativ`);
        // Tags: lange Namen kürzt der Chip mit Auslassungszeichen (`truncate`); der volle Name steht im `title` (A6-Fix, AD13).
        const huelle = modus === "tags" ? document.getElementById(`${a.id}-nativ`)?.parentElement : null;
        let beobachter: MutationObserver | null = null;
        if (huelle) {
          const setzeTitel = () => huelle.querySelectorAll<HTMLElement>("[data-title]").forEach((t) => {
            const text = t.textContent?.trim() ?? "";
            if (text !== "" && t.title !== text) t.title = text;
          });
          setzeTitel();
          beobachter = new MutationObserver(setzeTitel);
          beobachter.observe(huelle, { childList: true, subtree: true });
        }
        loesen = () => {
          aufheben?.();
          beobachter?.disconnect();
        };
      })
      .catch((fehler: unknown) => {
        console.error("Auswahl: Preline-Plugin nicht geladen", fehler);
      });
    return () => {
      abgebrochen = true;
      loesen?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `a.id` ist Teil des Schlüssels nur über die Id des Feldes
  }, [schluessel, eingehaengt, a.id]);

  const gruppen = [...new Set(optionen.map((o) => o.gruppe).filter((g): g is string => Boolean(g)))];
  const ohneGruppe = optionen.filter((o) => !o.gruppe);

  const optionsKnoten = (o: HsOption) => {
    const zusatz: Record<string, string> = {};
    if (o.symbolHtml) zusatz.icon = o.symbolHtml;
    if (o.beschreibung) zusatz.description = o.beschreibung;
    return (
      <option
        key={o.wert}
        value={o.wert}
        disabled={o.deaktiviert}
        data-hs-select-option={Object.keys(zusatz).length > 0 ? JSON.stringify(zusatz) : undefined}
      >
        {o.label}
      </option>
    );
  };

  return (
    // Der Container wird immer als Ganzes ausgetauscht (key), nie das select allein: Preline hängt das select um.
    <FeldHuelle key={schluessel}>
      {!eingehaengt ? (
        <>
          {/* Preline: component/advanced-select/advanced-select-search-inside-dropdown (Platzhalter bis zum Einhängen, Höhe des Auslösers) */}
          <div aria-hidden="true" className="w-full min-h-11 rounded-lg border border-layer-line bg-layer" />
        </>
      ) : (
      <>
      {/* Preline: component/advanced-select/advanced-select-search-inside-dropdown */}
      <select
        id={`${a.id}-nativ`}
        name={name}
        multiple={modus !== "einzeln"}
        defaultValue={vorgabe}
        disabled={deaktiviert}
        className="hidden"
        data-hs-select={JSON.stringify(konfiguration)}
        onChange={(e) => {
          if (!beiAenderung) return;
          if (modus === "einzeln") beiAenderung(e.target.value);
          else beiAenderung(Array.from(e.target.selectedOptions).map((o) => o.value));
        }}
      >
        {(leerLabel !== undefined || modus === "einzeln") && <option value="">{leerLabel ?? platzhalter}</option>}
        {ohneGruppe.map(optionsKnoten)}
        {gruppen.map((g) => (
          <optgroup key={g} label={g}>
            {optionen.filter((o) => o.gruppe === g).map(optionsKnoten)}
          </optgroup>
        ))}
      </select>
      </>
      )}
    </FeldHuelle>
  );
}

export interface AuswahlErweitertEigenschaften extends FeldEigenschaften, HsRest {
  optionen: HsOption[];
  platzhalter?: string;
  /** Suche im Dropdown erzwingen oder abschalten; Standard: ab `SUCHE_AB` Optionen. */
  mitSuche?: boolean;
  /** Mehrfachauswahl mit Zähler („3 ausgewählt“). */
  mehrfach?: boolean;
  zaehlerText?: string;
  /** Leere Option („— kein Zugewiesener —“). */
  leerLabel?: string;
}

export function AuswahlErweitert({
  optionen,
  platzhalter = "Bitte wählen",
  mitSuche,
  mehrfach = false,
  zaehlerText,
  leerLabel,
  name,
  vorgabe,
  deaktiviert,
  beiAenderung,
  ...feld
}: AuswahlErweitertEigenschaften) {
  return (
    <Feld {...feld}>
      {(a) => (
        <HsAuswahl
          attribute={a}
          modus={mehrfach ? "mehrfach" : "einzeln"}
          optionen={optionen}
          platzhalter={platzhalter}
          mitSuche={mitSuche ?? optionen.length >= SUCHE_AB}
          zaehlerText={zaehlerText}
          leerLabel={leerLabel}
          name={name}
          vorgabe={vorgabe}
          deaktiviert={deaktiviert}
          beiAenderung={beiAenderung}
        />
      )}
    </Feld>
  );
}
